import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { toast } from "sonner";
import { formatDate, formatApiError } from "../lib/format";
import {
  DIVISIONS, CONTENT_STATUSES, CONTENT_STATUS_LABEL, CONTENT_PLATFORMS, CONTENT_TYPES,
} from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import {
  Copy, ExternalLink, Plus, Trash2, Send, CheckCircle2, RotateCcw, Rocket, ShieldCheck, ShieldX,
  Sparkles, LinkIcon, FileText, ClipboardList, Camera, MessagesSquare, BarChart3, Wand2, Calendar,
  Search as SearchIcon, MapPin, User as UserIcon, AlertCircle,
} from "lucide-react";

const isContentMgr = (u) =>
  !!u && (["owner", "admin", "general_manager", "division_manager"].includes(u.role)
    || (u.role === "division_admin" && u.division_id === "div-mua"));

const BookingLinker = ({ content, mgr, onLinked }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [linked, setLinked] = useState(null);
  useEffect(() => {
    if (!content?.booking_id) { setLinked(null); return; }
    api.get(`/bookings/${content.booking_id}`).then(({ data }) => setLinked(data)).catch(() => {});
  }, [content?.booking_id]);
  useEffect(() => {
    if (!q || q.length < 2) { setRes(null); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setRes((data.bookings || []).slice(0, 5)); }
      catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const doLink = async (bid) => {
    try { const { data } = await api.post(`/content/${content.id}/link-booking`, { booking_id: bid });
      toast.success(bid ? "Terhubung ke booking" : "Tautan dilepas"); onLinked(data); setQ(""); setRes(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (content?.booking_id && linked) {
    return (
      <div className="flex items-start justify-between gap-2 bg-[#FAF6EE] border border-[#EAE3D6] rounded p-2">
        <div className="text-sm">
          <Link to={`/bookings/${linked.id}`} className="text-brand hover:underline font-medium">{linked.booking_code}</Link>
          <div className="text-[11px] text-[#5C6066]">{linked.client_name || linked.event_name} • {formatDate(linked.event_date)} • {linked.venue}</div>
        </div>
        {mgr && <Button size="sm" variant="outline" onClick={() => doLink(null)} data-testid="btn-unlink-booking">Lepas Tautan</Button>}
      </div>
    );
  }
  if (!mgr) return <div className="text-xs italic text-[#8C9096]">Belum terhubung ke booking apapun.</div>;
  return (
    <div>
      <div className="relative">
        <SearchIcon className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8C9096]"/>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari kode/klien/venue…" className="pl-8" data-testid="input-booking-search"/>
      </div>
      {res && res.length > 0 && (
        <div className="border border-[#EAE3D6] mt-1 rounded overflow-hidden">
          {res.map((b) => (
            <button key={b.id} onClick={() => doLink(b.id)} className="w-full text-left px-3 py-2 hover:bg-[#FAF6EE] flex items-center justify-between" data-testid={`booking-opt-${b.booking_code}`}>
              <div><div className="text-sm">{b.client_name || b.event_name}</div><div className="text-[11px] text-[#8C9096]">{b.venue || "-"} • {formatDate(b.event_date)}</div></div>
              <span className="text-[10px] font-mono text-brand">{b.booking_code}</span>
            </button>
          ))}
        </div>
      )}
      {res && res.length === 0 && q.length >= 2 && <div className="text-xs text-[#8C9096] mt-1 italic">Tidak ada hasil</div>}
    </div>
  );
};

const MustAdd = ({ contentId, refresh }) => {
  const [t, setT] = useState("");
  const add = async () => {
    if (!t.trim()) return;
    try { const { data } = await api.post(`/content/${contentId}/must-moments`, { text: t.trim() }); refresh(data); setT(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex gap-2 mt-2">
      <Input placeholder="Contoh: First look, pertukaran cincin, first dance..." value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} className="text-sm" data-testid="input-must"/>
      <Button size="sm" onClick={add} className="bg-brand text-white" data-testid="btn-add-must"><Plus className="w-4 h-4"/></Button>
    </div>
  );
};

const LiveAdd = ({ contentId, refresh }) => {
  const [t, setT] = useState("");
  const add = async () => {
    if (!t.trim()) return;
    try { const { data } = await api.post(`/content/${contentId}/live-prep`, { text: t.trim() }); refresh(data); setT(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex gap-2 mt-2">
      <Input placeholder="Contoh: Test streaming key 30 menit sebelum, cek sinyal..." value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} className="text-sm" data-testid="input-live"/>
      <Button size="sm" onClick={add} className="bg-brand text-white" data-testid="btn-add-live"><Plus className="w-4 h-4"/></Button>
    </div>
  );
};

const clsInput = "mt-1 text-sm";

export default function ContentDetailModal({ contentId, open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [ccList, setCcList] = useState([]);
  const [dirty, setDirty] = useState({});
  const [shotText, setShotText] = useState("");
  const [prodText, setProdText] = useState("");
  const [asset, setAsset] = useState({ label: "", url: "", kind: "draft", internal: false });
  const [comment, setComment] = useState({ note: "", internal: false });
  const [metric, setMetric] = useState({ views:"", reach:"", likes:"", comments_count:"", shares:"", saves:"", leads:"", note:"" });
  const [assignForm, setAssignForm] = useState({ assigned_creator_id: "", deadline: "", note: "" });
  const [submitNote, setSubmitNote] = useState("");
  const [previewLink, setPreviewLink] = useState("");
  const [revisionNote, setRevisionNote] = useState("");
  const [publishForm, setPublishForm] = useState({ link: "", published_date: "" });
  const [dupForm, setDupForm] = useState({ platform: "TikTok", content_type: "Reels", planned_date: "" });

  const mgr = isContentMgr(user);

  const load = async () => {
    if (!contentId) return;
    try {
      const { data } = await api.get(`/content/${contentId}`);
      setC(data); setDirty({});
      setPreviewLink(data.preview_link || "");
      setPublishForm({ link: data.link || "", published_date: data.published_date || "" });
      setAssignForm({ assigned_creator_id: data.assigned_creator_id || "", deadline: data.deadline || "", note: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail || "Gagal memuat")); }
  };
  useEffect(() => { if (open && contentId) { load(); if (mgr) api.get("/content/creators/list").then(({data}) => setCcList(data)).catch(()=>{}); } // eslint-disable-next-line
  }, [open, contentId]);

  const refresh = (d) => { setC(d); onChanged && onChanged(); };
  const set = (k, v) => setDirty((x) => ({ ...x, [k]: v }));
  const getField = (k) => (dirty[k] !== undefined ? dirty[k] : (c?.[k] ?? ""));

  const saveBrief = async () => {
    if (!Object.keys(dirty).length) return;
    try { const { data } = await api.put(`/content/${contentId}`, dirty);
      toast.success("Brief disimpan"); refresh(data); setDirty({});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const promoteBrief = async () => {
    try { const { data } = await api.post(`/content/${contentId}/promote-to-brief`);
      toast.success("Brief siap"); refresh(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doAssign = async () => {
    if (!assignForm.assigned_creator_id) return toast.error("Pilih Content Creator");
    try { const { data } = await api.post(`/content/${contentId}/assign`, assignForm);
      toast.success("Ditugaskan"); refresh(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doSubmitApproval = async () => {
    try { const { data } = await api.post(`/content/${contentId}/submit-for-approval`, { preview_link: previewLink, note: submitNote });
      toast.success("Diminta approval"); refresh(data); setSubmitNote("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doApprove = async () => {
    try { const { data } = await api.post(`/content/${contentId}/approve`, { note: "" });
      toast.success("Disetujui — dijadwalkan"); refresh(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doRevision = async () => {
    if (!revisionNote.trim()) return toast.error("Isi catatan revisi");
    try { const { data } = await api.post(`/content/${contentId}/request-revision`, { note: revisionNote.trim() });
      toast.success("Revisi dikirim ke CC"); refresh(data); setRevisionNote("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doPublish = async () => {
    if (!publishForm.link.trim()) return toast.error("Isi link publikasi");
    try { const { data } = await api.post(`/content/${contentId}/mark-published`, publishForm);
      toast.success("Ditandai terbit"); refresh(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDuplicate = async () => {
    try { const { data } = await api.post(`/content/${contentId}/duplicate`, dupForm);
      toast.success("Duplikasi dibuat"); onChanged && onChanged(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  // shot list
  const addShot = async () => { if (!shotText.trim()) return;
    try { const { data } = await api.post(`/content/${contentId}/shot-list`, { text: shotText.trim() });
      refresh(data); setShotText("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggleShot = async (id, done) => { const { data } = await api.put(`/content/${contentId}/shot-list/${id}`, { done }); refresh(data); };
  const delShot = async (id) => { const { data } = await api.delete(`/content/${contentId}/shot-list/${id}`); refresh(data); };
  // prod check
  const addProd = async () => { if (!prodText.trim()) return;
    try { const { data } = await api.post(`/content/${contentId}/prod-check`, { text: prodText.trim() });
      refresh(data); setProdText("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggleProd = async (id, done) => { const { data } = await api.put(`/content/${contentId}/prod-check/${id}`, { done }); refresh(data); };
  const delProd = async (id) => { const { data } = await api.delete(`/content/${contentId}/prod-check/${id}`); refresh(data); };
  // assets
  const addAsset = async () => {
    if (!asset.label.trim() || !asset.url.trim()) return toast.error("Label dan URL wajib");
    try { const { data } = await api.post(`/content/${contentId}/assets`, asset);
      refresh(data); setAsset({ label: "", url: "", kind: "draft", internal: false });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delAsset = async (id) => { const { data } = await api.delete(`/content/${contentId}/assets/${id}`); refresh(data); };
  // comments
  const addComment = async () => {
    if (!comment.note.trim()) return;
    try { const { data } = await api.post(`/content/${contentId}/comments`, comment);
      refresh(data); setComment({ note: "", internal: false });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  // metrics
  const addMetric = async () => {
    const payload = Object.fromEntries(Object.entries(metric).map(([k,v]) => [k, v === "" ? null : (isNaN(v) ? v : Number(v))]));
    try { const { data } = await api.post(`/content/${contentId}/metrics`, payload);
      toast.success("Metrik dicatat"); refresh(data);
      setMetric({ views:"", reach:"", likes:"", comments_count:"", shares:"", saves:"", leads:"", note:"" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  // caption tools
  const capChars = (getField("caption_draft") || "").length;
  const copyCaption = async () => { try { await navigator.clipboard.writeText(getField("caption_draft") || ""); toast.success("Caption disalin"); } catch { toast.error("Gagal salin"); } };

  if (!c && !contentId) return null;
  const div = c ? DIVISIONS.find((d) => d.id === c.division_id) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="content-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span>{c?.title || "Memuat…"}</span>
            {c && <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand/10 text-brand border border-brand/30">{CONTENT_STATUS_LABEL[c.status] || c.status}</span>}
            {c?.brief_ready && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">BRIEF READY</span>}
            {c?.approval_status === "Approved" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 border border-sky-200">APPROVED</span>}
            {div && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${div.color}`}>{div.short}</span>}
          </DialogTitle>
        </DialogHeader>

        {c && (
          <Tabs defaultValue="brief" className="w-full">
            <TabsList className="w-full grid grid-cols-7">
              <TabsTrigger value="brief" data-testid="tab-brief"><FileText className="w-3.5 h-3.5 mr-1"/>Brief</TabsTrigger>
              <TabsTrigger value="shot" data-testid="tab-shot"><Camera className="w-3.5 h-3.5 mr-1"/>Shot List</TabsTrigger>
              <TabsTrigger value="prod" data-testid="tab-prod"><ClipboardList className="w-3.5 h-3.5 mr-1"/>Persiapan</TabsTrigger>
              <TabsTrigger value="event" data-testid="tab-event"><Calendar className="w-3.5 h-3.5 mr-1"/>Event</TabsTrigger>
              <TabsTrigger value="assets" data-testid="tab-assets"><LinkIcon className="w-3.5 h-3.5 mr-1"/>Aset</TabsTrigger>
              <TabsTrigger value="review" data-testid="tab-review"><MessagesSquare className="w-3.5 h-3.5 mr-1"/>Review</TabsTrigger>
              <TabsTrigger value="metrics" data-testid="tab-metrics"><BarChart3 className="w-3.5 h-3.5 mr-1"/>Metrik</TabsTrigger>
            </TabsList>

            {/* BRIEF */}
            <TabsContent value="brief" className="pt-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Judul</Label><Input className={clsInput} value={getField("title")} onChange={(e) => set("title", e.target.value)} data-testid="brief-title"/></div>
                <div><Label>Divisi / Brand</Label>
                  <Select value={getField("division_id")} onValueChange={(v) => set("division_id", v)}>
                    <SelectTrigger className={clsInput}><SelectValue placeholder="Pilih" /></SelectTrigger>
                    <SelectContent>{DIVISIONS.map((d) => <SelectItem key={d.id} value={d.id}>{d.short}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Pilar Konten</Label><Input className={clsInput} placeholder="Portofolio / Edukasi / Testimoni" value={getField("content_pillar")} onChange={(e) => set("content_pillar", e.target.value)}/></div>
                <div><Label>Tipe</Label>
                  <Select value={getField("content_type")} onValueChange={(v) => set("content_type", v)}>
                    <SelectTrigger className={clsInput}><SelectValue/></SelectTrigger>
                    <SelectContent>{CONTENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Platform</Label>
                  <Select value={getField("platform")} onValueChange={(v) => set("platform", v)}>
                    <SelectTrigger className={clsInput}><SelectValue/></SelectTrigger>
                    <SelectContent>{CONTENT_PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Rencana Tayang</Label><Input type="date" className={clsInput} value={getField("planned_date")} onChange={(e) => set("planned_date", e.target.value)}/></div>
                <div><Label>Deadline Produksi</Label><Input type="date" className={clsInput} value={getField("deadline") || ""} onChange={(e) => set("deadline", e.target.value)}/></div>
                <div><Label>Durasi (detik)</Label><Input type="number" className={clsInput} value={getField("duration_sec") || ""} onChange={(e) => set("duration_sec", e.target.value ? Number(e.target.value) : null)}/></div>
                <div><Label>Rasio</Label><Input className={clsInput} placeholder="9:16 / 1:1 / 4:5" value={getField("aspect_ratio")} onChange={(e) => set("aspect_ratio", e.target.value)}/></div>
                <div className="col-span-2"><Label>Ringkasan Ide</Label><Textarea rows={2} className={clsInput} value={getField("idea_summary")} onChange={(e) => set("idea_summary", e.target.value)}/></div>
                <div className="col-span-2"><Label>Objektif <span className="text-rose-500">*</span></Label><Input className={clsInput} value={getField("objective")} onChange={(e) => set("objective", e.target.value)} data-testid="brief-objective"/></div>
                <div className="col-span-2"><Label>Target Audience</Label><Input className={clsInput} placeholder="Bride to be 22-30, Jakarta" value={getField("target_audience")} onChange={(e) => set("target_audience", e.target.value)}/></div>
                <div className="col-span-2"><Label>Pesan Utama <span className="text-rose-500">*</span></Label><Input className={clsInput} value={getField("key_message")} onChange={(e) => set("key_message", e.target.value)} data-testid="brief-keymessage"/></div>
                <div><Label>Hook (3 detik pertama) <span className="text-rose-500">*</span></Label><Input className={clsInput} value={getField("hook")} onChange={(e) => set("hook", e.target.value)} data-testid="brief-hook"/></div>
                <div><Label>CTA <span className="text-rose-500">*</span></Label><Input className={clsInput} value={getField("cta")} onChange={(e) => set("cta", e.target.value)} data-testid="brief-cta"/></div>
                <div className="col-span-2"><Label>Script / Alur</Label><Textarea rows={3} className={clsInput} value={getField("script")} onChange={(e) => set("script", e.target.value)}/></div>
                <div className="col-span-2">
                  <div className="flex items-center justify-between">
                    <Label>Draft Caption</Label>
                    <div className="text-[11px] text-[#8C9096] flex items-center gap-2">
                      <span>{capChars} karakter</span>
                      <button type="button" onClick={copyCaption} className="text-brand hover:underline flex items-center gap-1"><Copy className="w-3 h-3"/>Salin</button>
                    </div>
                  </div>
                  <Textarea rows={3} className={clsInput} value={getField("caption_draft")} onChange={(e) => set("caption_draft", e.target.value)} data-testid="brief-caption"/>
                </div>
                <div className="col-span-2"><Label>Hashtags</Label><Input className={clsInput} value={getField("hashtags")} onChange={(e) => set("hashtags", e.target.value)}/></div>
                <div className="col-span-2"><Label>Referensi (URL, pisah dengan spasi)</Label>
                  <Input className={clsInput} value={(getField("references") || []).join(" ")} onChange={(e) => set("references", e.target.value.split(/\s+/).filter(Boolean))} placeholder="https://..."/>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-[#EAE3D6]">
                <div className="flex items-center gap-2 flex-wrap">
                  {mgr && <Button size="sm" onClick={saveBrief} disabled={!Object.keys(dirty).length} className="bg-brand text-white" data-testid="btn-save-brief">Simpan Brief</Button>}
                  {mgr && !c.brief_ready && <Button size="sm" variant="outline" onClick={promoteBrief} data-testid="btn-promote-brief"><Sparkles className="w-3.5 h-3.5 mr-1"/>Tandai Brief Siap</Button>}
                </div>
              </div>

              {/* Assignment */}
              {mgr && (
                <div className="border-t border-[#EAE3D6] pt-3 space-y-2">
                  <div className="font-semibold text-sm">Penugasan CC</div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Select value={assignForm.assigned_creator_id} onValueChange={(v) => setAssignForm({...assignForm, assigned_creator_id: v})}>
                        <SelectTrigger className={clsInput} data-testid="assign-cc-select"><SelectValue placeholder="Pilih CC"/></SelectTrigger>
                        <SelectContent>{ccList.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} — {u.email}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Input type="date" placeholder="Deadline" className={clsInput} value={assignForm.deadline} onChange={(e) => setAssignForm({...assignForm, deadline: e.target.value})}/>
                    <Button size="sm" onClick={doAssign} className="bg-brand text-white" data-testid="btn-assign-cc"><Rocket className="w-3.5 h-3.5 mr-1"/>Assign</Button>
                  </div>
                  <Input placeholder="Catatan penugasan (opsional)" className={clsInput} value={assignForm.note} onChange={(e) => setAssignForm({...assignForm, note: e.target.value})}/>
                </div>
              )}

              {/* Duplicate */}
              {(mgr || user?.role === "content_creator") && (
                <div className="border-t border-[#EAE3D6] pt-3 space-y-2">
                  <div className="font-semibold text-sm flex items-center gap-1"><Wand2 className="w-4 h-4"/>Duplikasi ke platform lain</div>
                  <div className="grid grid-cols-4 gap-2">
                    <Select value={dupForm.platform} onValueChange={(v) => setDupForm({...dupForm, platform: v})}>
                      <SelectTrigger className={clsInput}><SelectValue/></SelectTrigger>
                      <SelectContent>{CONTENT_PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={dupForm.content_type} onValueChange={(v) => setDupForm({...dupForm, content_type: v})}>
                      <SelectTrigger className={clsInput}><SelectValue/></SelectTrigger>
                      <SelectContent>{CONTENT_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="date" placeholder="Tanggal" className={clsInput} value={dupForm.planned_date} onChange={(e) => setDupForm({...dupForm, planned_date: e.target.value})}/>
                    <Button size="sm" variant="outline" onClick={doDuplicate} data-testid="btn-duplicate-content">Duplikasi</Button>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* SHOT LIST */}
            <TabsContent value="shot" className="pt-3 space-y-2">
              <div className="text-xs text-[#8C9096]">Ceklis shot / take saat produksi. Setiap item dicatat siapa & kapan diselesaikan.</div>
              <div className="space-y-1.5">
                {(c.shot_list || []).map((it) => (
                  <div key={it.id} className="flex items-start gap-2 group" data-testid={`shot-${it.id}`}>
                    <Checkbox checked={!!it.done} onCheckedChange={(v) => toggleShot(it.id, !!v)}/>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${it.done ? "line-through text-[#8C9096]" : ""}`}>{it.text}</div>
                      {it.done && <div className="text-[10px] text-[#8C9096]">✓ {it.done_by} • {formatDate(it.done_at)}</div>}
                    </div>
                    <button onClick={() => delShot(it.id)} className="opacity-0 group-hover:opacity-100 text-rose-500 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5"/></button>
                  </div>
                ))}
                {(c.shot_list || []).length === 0 && <div className="text-xs text-[#8C9096] italic">Belum ada shot.</div>}
              </div>
              <div className="flex gap-2">
                <Input placeholder="Contoh: Close-up cincin sambil putar 360°" value={shotText} onChange={(e) => setShotText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addShot()} className={clsInput} data-testid="input-shot"/>
                <Button size="sm" onClick={addShot} className="bg-brand text-white" data-testid="btn-add-shot"><Plus className="w-4 h-4"/></Button>
              </div>
            </TabsContent>

            {/* PRODUCTION CHECKLIST */}
            <TabsContent value="prod" className="pt-3 space-y-2">
              <div className="text-xs text-[#8C9096]">Checklist alat, talent, lokasi, produk/layanan yang dibutuhkan.</div>
              <div className="space-y-1.5">
                {(c.production_checklist || []).map((it) => (
                  <div key={it.id} className="flex items-start gap-2 group" data-testid={`prod-${it.id}`}>
                    <Checkbox checked={!!it.done} onCheckedChange={(v) => toggleProd(it.id, !!v)}/>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${it.done ? "line-through text-[#8C9096]" : ""}`}>{it.text}</div>
                      {it.done && <div className="text-[10px] text-[#8C9096]">✓ {it.done_by} • {formatDate(it.done_at)}</div>}
                    </div>
                    <button onClick={() => delProd(it.id)} className="opacity-0 group-hover:opacity-100 text-rose-500 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5"/></button>
                  </div>
                ))}
                {(c.production_checklist || []).length === 0 && <div className="text-xs text-[#8C9096] italic">Belum ada persiapan.</div>}
              </div>
              <div className="flex gap-2">
                <Input placeholder="Contoh: Ring light, Kamera A7III, Talent Riri" value={prodText} onChange={(e) => setProdText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addProd()} className={clsInput} data-testid="input-prod"/>
                <Button size="sm" onClick={addProd} className="bg-brand text-white" data-testid="btn-add-prod"><Plus className="w-4 h-4"/></Button>
              </div>
            </TabsContent>

            {/* EVENT & LIVE COVERAGE */}
            <TabsContent value="event" className="pt-3 space-y-3">
              <div className="text-xs text-[#8C9096]">Hubungkan konten dengan booking (acara). Lokasi/PIC auto-terisi dari data booking bila kosong.</div>

              <div className="border border-[#EAE3D6] rounded p-3 bg-white space-y-2">
                <div className="text-sm font-semibold flex items-center gap-1"><Calendar className="w-4 h-4"/>Booking Terhubung</div>
                <BookingLinker content={c} mgr={mgr} onLinked={refresh}/>
              </div>

              {c.booking_id && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Lokasi Acara</Label><Input className={clsInput} value={getField("event_location")} onChange={(e) => set("event_location", e.target.value)} data-testid="event-location"/></div>
                  <div><Label className="text-xs">PIC Lapangan</Label><Input className={clsInput} placeholder="Nama koordinator" value={getField("event_pic")} onChange={(e) => set("event_pic", e.target.value)} data-testid="event-pic"/></div>
                  <div className="col-span-2"><Label className="text-xs">Rundown / Ringkasan</Label><Textarea rows={3} className={clsInput} value={getField("event_rundown")} onChange={(e) => set("event_rundown", e.target.value)} placeholder="09.00 Prewedding, 11.00 Akad, 13.00 Resepsi..." data-testid="event-rundown"/></div>
                  <div className="col-span-2"><Label className="text-xs">Batasan Dokumentasi</Label><Textarea rows={2} className={clsInput} value={getField("documentation_limits")} onChange={(e) => set("documentation_limits", e.target.value)} placeholder="Tidak boleh foto ijab kabul, dsb." data-testid="event-limits"/></div>
                  <div className="col-span-2 flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox checked={!!getField("same_day_delivery")} onCheckedChange={(v) => set("same_day_delivery", !!v)} data-testid="event-samedelivery"/>
                      Same-day delivery
                    </label>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Deadline H+</Label>
                      <Input type="number" className={`${clsInput} w-24`} value={getField("hplus_hours") || ""} onChange={(e) => set("hplus_hours", e.target.value ? Number(e.target.value) : null)} placeholder="48"/>
                      <span className="text-xs text-[#8C9096]">jam setelah acara</span>
                    </div>
                  </div>
                  <div className="col-span-2 flex justify-end">
                    {mgr && <Button size="sm" onClick={saveBrief} disabled={!Object.keys(dirty).length} className="bg-brand text-white" data-testid="btn-save-event">Simpan Detail Event</Button>}
                  </div>
                </div>
              )}

              {/* Must moments */}
              <div className="border-t border-[#EAE3D6] pt-3">
                <div className="text-sm font-semibold flex items-center gap-1 mb-2"><AlertCircle className="w-4 h-4 text-rose-600"/>Momen Wajib</div>
                <div className="space-y-1.5">
                  {(c.must_moments || []).map((it) => (
                    <div key={it.id} className="flex items-start gap-2 group" data-testid={`must-${it.id}`}>
                      <Checkbox checked={!!it.done} onCheckedChange={async (v) => { const { data } = await api.put(`/content/${contentId}/must-moments/${it.id}`, { done: !!v }); refresh(data); }}/>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm ${it.done ? "line-through text-[#8C9096]" : ""}`}>{it.text}</div>
                        {it.done && <div className="text-[10px] text-[#8C9096]">✓ {it.done_by} • {formatDate(it.done_at)}</div>}
                      </div>
                      <button onClick={async () => { const { data } = await api.delete(`/content/${contentId}/must-moments/${it.id}`); refresh(data); }} className="opacity-0 group-hover:opacity-100 text-rose-500"><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                  ))}
                  {(c.must_moments || []).length === 0 && <div className="text-xs text-[#8C9096] italic">Belum ada momen wajib.</div>}
                </div>
                <MustAdd contentId={contentId} refresh={refresh}/>
              </div>

              {/* Live prep */}
              <div className="border-t border-[#EAE3D6] pt-3">
                <div className="text-sm font-semibold flex items-center gap-1 mb-2"><ClipboardList className="w-4 h-4 text-brand"/>Persiapan Live</div>
                <div className="space-y-1.5">
                  {(c.live_prep || []).map((it) => (
                    <div key={it.id} className="flex items-start gap-2 group" data-testid={`live-${it.id}`}>
                      <Checkbox checked={!!it.done} onCheckedChange={async (v) => { const { data } = await api.put(`/content/${contentId}/live-prep/${it.id}`, { done: !!v }); refresh(data); }}/>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm ${it.done ? "line-through text-[#8C9096]" : ""}`}>{it.text}</div>
                        {it.done && <div className="text-[10px] text-[#8C9096]">✓ {it.done_by} • {formatDate(it.done_at)}</div>}
                      </div>
                      <button onClick={async () => { const { data } = await api.delete(`/content/${contentId}/live-prep/${it.id}`); refresh(data); }} className="opacity-0 group-hover:opacity-100 text-rose-500"><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                  ))}
                  {(c.live_prep || []).length === 0 && <div className="text-xs text-[#8C9096] italic">Belum ada persiapan live.</div>}
                </div>
                <LiveAdd contentId={contentId} refresh={refresh}/>
              </div>
            </TabsContent>

            {/* ASSETS */}
            <TabsContent value="assets" className="pt-3 space-y-3">
              <div className="text-xs text-[#8C9096]">Tautan file (Google Drive, Dropbox, dsb) dengan tipe (draft/final/referensi). Versi dihitung per kind.</div>
              <div className="space-y-1.5">
                {(c.assets || []).map((a) => (
                  <div key={a.id} className="flex items-start gap-2 border border-[#EAE3D6] rounded p-2 bg-white" data-testid={`asset-${a.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FAF6EE] text-[#3C4045] border border-[#EAE3D6] uppercase">{a.kind} v{a.version}</span>
                        {a.internal && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">INTERNAL</span>}
                        <span className="text-sm font-medium">{a.label}</span>
                      </div>
                      <a href={a.url} target="_blank" rel="noreferrer" className="text-[11px] text-brand break-all hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3"/>{a.url}</a>
                      <div className="text-[10px] text-[#8C9096]">oleh {a.uploaded_by} • {formatDate(a.uploaded_at)}</div>
                    </div>
                    <button onClick={() => delAsset(a.id)} className="text-rose-500 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5"/></button>
                  </div>
                ))}
                {(c.assets || []).length === 0 && <div className="text-xs text-[#8C9096] italic">Belum ada aset.</div>}
              </div>
              <div className="grid grid-cols-6 gap-2 items-end border-t border-[#EAE3D6] pt-3">
                <div className="col-span-2"><Label className="text-xs">Label</Label><Input className={clsInput} placeholder="Draft V1" value={asset.label} onChange={(e) => setAsset({...asset, label: e.target.value})} data-testid="asset-label"/></div>
                <div className="col-span-3"><Label className="text-xs">URL</Label><Input className={clsInput} placeholder="https://drive.google.com/…" value={asset.url} onChange={(e) => setAsset({...asset, url: e.target.value})} data-testid="asset-url"/></div>
                <div>
                  <Select value={asset.kind} onValueChange={(v) => setAsset({...asset, kind: v})}>
                    <SelectTrigger className={clsInput}><SelectValue/></SelectTrigger>
                    <SelectContent>{["reference","draft","final","file","footage","audio"].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {mgr && (
                  <label className="col-span-5 flex items-center gap-2 text-xs text-[#5C6066]">
                    <Checkbox checked={!!asset.internal} onCheckedChange={(v) => setAsset({...asset, internal: !!v})}/>
                    Aset internal (tidak dibagikan ke klien)
                  </label>
                )}
                <Button size="sm" onClick={addAsset} className="bg-brand text-white" data-testid="btn-add-asset"><Plus className="w-4 h-4 mr-1"/>Tambah</Button>
              </div>
            </TabsContent>

            {/* REVIEW & Workflow */}
            <TabsContent value="review" className="pt-3 space-y-3">
              {/* Workflow actions */}
              <div className="grid grid-cols-1 gap-2">
                <div className="border border-[#EAE3D6] rounded p-3 bg-white space-y-2">
                  <div className="text-sm font-semibold">Alur Approval</div>
                  <div className="text-xs text-[#8C9096]">Status saat ini: <b className="text-[#3C4045]">{CONTENT_STATUS_LABEL[c.status] || c.status}</b></div>
                  {["Idea","Planned","Production","Editing"].includes(c.status) && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input placeholder="Preview link (opsional)" className={clsInput} value={previewLink} onChange={(e) => setPreviewLink(e.target.value)}/>
                      <Input placeholder="Catatan (opsional)" className={clsInput} value={submitNote} onChange={(e) => setSubmitNote(e.target.value)}/>
                      <Button size="sm" onClick={doSubmitApproval} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-submit-approval">
                        <Send className="w-3.5 h-3.5 mr-1"/>Minta Approval
                      </Button>
                    </div>
                  )}
                  {c.status === "Waiting Approval" && mgr && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button size="sm" onClick={doApprove} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-approve-content"><ShieldCheck className="w-3.5 h-3.5 mr-1"/>Setujui</Button>
                      <Input placeholder="Alasan revisi…" className={clsInput} value={revisionNote} onChange={(e) => setRevisionNote(e.target.value)}/>
                      <Button size="sm" variant="outline" onClick={doRevision} className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="btn-request-revision"><RotateCcw className="w-3.5 h-3.5 mr-1"/>Minta Revisi</Button>
                    </div>
                  )}
                  {c.status === "Waiting Approval" && !mgr && (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Menunggu approval dari Management/Manager Divisi.</div>
                  )}
                  {["Scheduled","Published"].includes(c.status) && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input placeholder="Link posting" className={clsInput} value={publishForm.link} onChange={(e) => setPublishForm({...publishForm, link: e.target.value})} data-testid="input-publish-link"/>
                      <Input type="date" className={clsInput} value={publishForm.published_date || ""} onChange={(e) => setPublishForm({...publishForm, published_date: e.target.value})}/>
                      <Button size="sm" onClick={doPublish} className="bg-brand text-white" data-testid="btn-mark-published"><CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Tandai Terbit</Button>
                    </div>
                  )}
                  {c.preview_link && <div className="text-xs">Preview: <a href={c.preview_link} target="_blank" rel="noreferrer" className="text-brand hover:underline break-all">{c.preview_link}</a></div>}
                  {c.link && c.status === "Published" && <div className="text-xs">Terbit: <a href={c.link} target="_blank" rel="noreferrer" className="text-brand hover:underline break-all">{c.link}</a> {c.published_date && `• ${formatDate(c.published_date)}`}</div>}
                </div>

                {/* Revisions history */}
                {(c.revisions || []).length > 0 && (
                  <div className="border border-amber-200 bg-amber-50 rounded p-3">
                    <div className="text-sm font-semibold text-amber-900 mb-1 flex items-center gap-1"><ShieldX className="w-4 h-4"/>Riwayat Revisi ({c.revisions.length})</div>
                    {c.revisions.map((r) => (
                      <div key={r.id} className="text-xs border-t border-amber-200 first:border-none pt-1.5">
                        <div className="text-amber-900">{r.note}</div>
                        <div className="text-[10px] text-amber-700">{r.by} • {formatDate(r.at)}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Comments */}
                <div className="border border-[#EAE3D6] rounded p-3 bg-white space-y-2">
                  <div className="text-sm font-semibold">Diskusi</div>
                  <div className="space-y-1.5 max-h-52 overflow-y-auto">
                    {(c.comments || []).map((cm) => (
                      <div key={cm.id} className={`text-sm border rounded p-2 ${cm.internal ? "border-amber-200 bg-amber-50/40" : "border-[#EAE3D6] bg-[#FAFAF7]"}`}>
                        <div className="flex items-center gap-2 text-[11px] text-[#8C9096]">
                          <b className="text-[#3C4045]">{cm.by}</b>
                          {cm.internal && <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">INTERNAL</span>}
                          <span>• {formatDate(cm.created_at)}</span>
                        </div>
                        <div>{cm.note}</div>
                      </div>
                    ))}
                    {(c.comments || []).length === 0 && <div className="text-xs italic text-[#8C9096]">Belum ada komentar.</div>}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Textarea rows={2} placeholder="Tulis komentar…" value={comment.note} onChange={(e) => setComment({...comment, note: e.target.value})} className={clsInput} data-testid="input-comment"/>
                    <div className="flex items-center justify-between">
                      {mgr && (
                        <label className="flex items-center gap-2 text-xs text-[#5C6066]">
                          <Checkbox checked={!!comment.internal} onCheckedChange={(v) => setComment({...comment, internal: !!v})}/>
                          Catatan internal (CC tidak melihat)
                        </label>
                      )}
                      <Button size="sm" onClick={addComment} disabled={!comment.note.trim()} className="bg-brand text-white ml-auto" data-testid="btn-add-comment">Kirim</Button>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* METRICS */}
            <TabsContent value="metrics" className="pt-3 space-y-3">
              <div className="text-xs text-[#8C9096]">Catat metrik manual per pengukuran. Kolom kosong = tidak tersedia (bukan nol). Belum tersedia sinkron otomatis dengan platform.</div>
              <div className="grid grid-cols-4 gap-2">
                {["views","reach","likes","comments_count","shares","saves","leads"].map((k) => (
                  <div key={k}>
                    <Label className="text-xs capitalize">{k.replace("_count","")}</Label>
                    <Input type="number" className={clsInput} value={metric[k]} onChange={(e) => setMetric({...metric, [k]: e.target.value})} data-testid={`metric-${k}`}/>
                  </div>
                ))}
                <div className="col-span-4">
                  <Label className="text-xs">Catatan (opsional)</Label>
                  <Input className={clsInput} value={metric.note} onChange={(e) => setMetric({...metric, note: e.target.value})}/>
                </div>
              </div>
              <div className="flex justify-end">
                <Button size="sm" onClick={addMetric} className="bg-brand text-white" data-testid="btn-add-metric"><Plus className="w-3.5 h-3.5 mr-1"/>Catat Metrik</Button>
              </div>
              {(c.metrics || []).length > 0 && (
                <div className="border border-[#EAE3D6] rounded overflow-hidden mt-2">
                  <table className="w-full text-xs">
                    <thead className="bg-[#FAF6EE] text-[#8C9096] uppercase">
                      <tr><th className="text-left px-2 py-1.5">Tanggal</th><th>Views</th><th>Reach</th><th>Likes</th><th>Comm</th><th>Shares</th><th>Saves</th><th>Leads</th></tr>
                    </thead>
                    <tbody>
                      {c.metrics.slice().reverse().map((m) => (
                        <tr key={m.id} className="border-t border-[#F0EBE0]">
                          <td className="px-2 py-1">{formatDate(m.recorded_at)}</td>
                          <td className="text-center">{m.views ?? "—"}</td>
                          <td className="text-center">{m.reach ?? "—"}</td>
                          <td className="text-center">{m.likes ?? "—"}</td>
                          <td className="text-center">{m.comments_count ?? "—"}</td>
                          <td className="text-center">{m.shares ?? "—"}</td>
                          <td className="text-center">{m.saves ?? "—"}</td>
                          <td className="text-center">{m.leads ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {c.booking_id && (
                <div className="text-xs text-[#8C9096] border-t border-[#EAE3D6] pt-2">
                  Terhubung ke booking: <Link to={`/bookings/${c.booking_id}`} className="text-brand hover:underline">buka acara</Link>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Tutup</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
